import { useRef, useState, type ReactNode } from "react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { ItemHistoryDetailLine } from "./ItemHistoryDetailLine";
import { ItemHistoryTextDetail } from "./ItemHistoryTextDetail";
import type { ItemHistoryDetail } from "./itemHistoryRows";

interface ItemHistoryDetailPopoverProps {
  children: ReactNode;
  className: string;
  details: readonly ItemHistoryDetail[];
  onSelectLine: (line: ItemHistoryDetail) => void;
  canOpen?: (trigger: HTMLButtonElement) => boolean;
  onClickClosed?: () => void;
}

/**
 * A trigger that opens an event's detail lines. Hover previews the detail and
 * a click, tap, or Enter pins it; a pinned popover takes focus so its lines
 * are reachable by keyboard. Selecting a line jumps to it and closes the
 * popover.
 * @param children - The trigger's content, such as "12 fields" or a cut value
 * @param className - The trigger's classes
 * @param details - The lines the popover lists
 * @param onSelectLine - Jumps the form to a selected line's field
 * @param canOpen - Checked at hover and click time; the popover opens only
 *   when it returns true. Opens always when omitted.
 * @param onClickClosed - Runs on a click when the popover cannot open
 */
export const ItemHistoryDetailPopover = ({
  children,
  className,
  details,
  onSelectLine,
  canOpen = () => true,
  onClickClosed,
}: ItemHistoryDetailPopoverProps) => {
  const [pinned, setPinned] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [trigger, setTrigger] = useState<HTMLButtonElement | null>(null);
  // Focus goes back to the chip only when the popover had taken it.
  const returnFocusRef = useRef(false);

  const dismiss = () => {
    returnFocusRef.current = pinned;
    setPinned(false);
    setPreviewing(false);
  };
  const lone = details.length === 1 && details[0].type === "text";

  return (
    <Popover
      open={pinned || previewing}
      onOpenChange={(open) => {
        if (!open) {
          dismiss();
        }
      }}
    >
      <PopoverTrigger asChild>
        <button
          ref={setTrigger}
          type="button"
          className={className}
          onClick={(event) => {
            // The popover's own toggle would close a hover-opened popover.
            event.preventDefault();
            if (pinned) {
              dismiss();
              return;
            }
            if (!canOpen(event.currentTarget)) {
              onClickClosed?.();
              return;
            }
            setPinned(true);
          }}
          onMouseEnter={(event) => {
            if (canOpen(event.currentTarget)) {
              setPreviewing(true);
            }
          }}
          onMouseLeave={() => setPreviewing(false)}
        >
          {children}
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        sideOffset={4}
        // Inside the editor's modal sheet, a popover portaled to the body
        // loses wheel scrolling to the sheet's scroll lock.
        container={
          trigger?.closest<HTMLElement>('[role="dialog"]') ?? undefined
        }
        className="max-h-72 w-[min(24rem,calc(100vw-2rem))] overflow-y-auto overscroll-contain p-1.5"
        onOpenAutoFocus={(event) => {
          if (!pinned) {
            event.preventDefault();
          }
        }}
        onCloseAutoFocus={(event) => {
          if (!returnFocusRef.current) {
            event.preventDefault();
          }
        }}
        onMouseEnter={() => setPreviewing(true)}
        onMouseLeave={() => setPreviewing(false)}
      >
        <div className="flex flex-col gap-y-px px-1.5 py-0.5">
          {details.map((line) => {
            if (line.type === "text" && !lone) {
              return <ItemHistoryTextDetail key={line.key} line={line} />;
            }
            const jumps = "target" in line && line.target !== null;
            return (
              <ItemHistoryDetailLine
                key={line.key}
                line={line}
                onSelect={
                  jumps
                    ? () => {
                        dismiss();
                        onSelectLine(line);
                      }
                    : undefined
                }
              />
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
};
